/**
 * Browser-side OpenTelemetry tracing, exported to PostHog's OTLP receiver.
 *
 * This is separate from the `tracing_headers` option in `./posthog.ts`. That one stamps
 * `X-POSTHOG-SESSION-ID` / `X-POSTHOG-DISTINCT-ID` on API requests so backend *events* correlate
 * to a session. This module emits *spans*: a fetch here becomes the root of a trace that the API
 * continues, via the W3C `traceparent` header, so one waterfall covers click -> resolver -> SQL.
 *
 * Import this module for its side effect, after `./posthog.ts` (the span processor below reads
 * the live session id from the posthog client).
 */
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from "@opentelemetry/sdk-trace-base";
import type { Span } from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
import posthog from "posthog-js";

import config from "./config";

const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string | undefined;
const posthogHost =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  "https://us.i.posthog.com";

export const TRACES_ENDPOINT = `${posthogHost.replace(/\/$/, "")}/i/v1/traces`;

const sampleRate = Number(
  (import.meta.env.VITE_OTEL_TRACES_SAMPLE_RATE as string | undefined) ?? "1"
);

/**
 * URLs that must never be instrumented.
 *
 * The traces endpoint is the important one: the exporter POSTs spans with `fetch`, so without
 * this the export itself creates a span, which schedules another export, forever. PostHog's
 * event/replay ingestion and the other telemetry vendors are excluded for noise, not safety.
 */
export const IGNORED_URLS: RegExp[] = [
  /\/i\/v1\/traces/,
  new RegExp(escapeRegExp(posthogHost)),
  /\/\/[^/]*posthog\.com/,
  /\/\/[^/]*sentry\.io/,
  /\/\/js\.stripe\.com/,
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Copy the PostHog identity onto every span as it starts.
 *
 * Must happen in `onStart`, not at init: the session id rotates (30 min idle by default) and the
 * distinct id changes on login, so a value captured once at boot would be wrong for most spans.
 */
export class PostHogIdentitySpanProcessor extends BatchSpanProcessor {
  onStart(
    span: Span,
    parentContext: Parameters<BatchSpanProcessor["onStart"]>[1]
  ): void {
    try {
      const sessionId = posthog.get_session_id?.();
      const distinctId = posthog.get_distinct_id?.();
      if (sessionId) {
        span.setAttribute("$session_id", sessionId);
        span.setAttribute("posthogSessionId", sessionId);
      }
      if (distinctId) {
        span.setAttribute("posthogDistinctId", distinctId);
      }
    } catch {
      // Identity is a nice-to-have; never let it break span creation.
    }
    super.onStart(span, parentContext);
  }
}

export function initTracing(): boolean {
  // Same guard as posthog.ts. Tests never set the token, so this is also what keeps the
  // global `fetch` unpatched under vitest (MSW also intercepts fetch; both would conflict).
  if (!token) return false;

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: "tsg-web",
      [ATTR_SERVICE_VERSION]: config.SHA,
      "deployment.environment": config.ENV,
      "deployment.environment.name": config.ENV,
    }),
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(
        Number.isFinite(sampleRate) ? Math.min(Math.max(sampleRate, 0), 1) : 1
      ),
    }),
    spanProcessors: [
      new PostHogIdentitySpanProcessor(
        new OTLPTraceExporter({
          url: TRACES_ENDPOINT,
          headers: { Authorization: `Bearer ${token}` },
        })
      ),
    ],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Without this, `traceparent` is only sent same-origin, so the API span would be its
        // own root instead of a child. app.* -> api.* is cross-origin, so this is required.
        propagateTraceHeaderCorsUrls: [
          new RegExp(escapeRegExp(config.API_HOST)),
        ],
        ignoreUrls: IGNORED_URLS,
        clearTimingResources: true,
      }),
    ],
  });

  return true;
}

export default initTracing();
